/**
 * Shared "view" JSON contract for Dynamic Pages & Views.
 *
 * This contract is the single source of truth shared by the renderer (DynamicPage),
 * the builder (PageEditorPage builder mode), and import/export.
 *
 * A view is a RECURSIVE WIDGET TREE: `{ widgets: Widget[] }`. Each widget is a
 * discriminated union on `type`. A `table` widget is SELF-CONTAINED — it carries its
 * own data binding (collection + relations + selected fields) as well as its column
 * mapping, page size and filterable fields. A `tabs` widget is a CONTAINER whose each
 * tab holds ANOTHER widget (recursion), so Tabs-in-Tabs is a valid JSON shape even
 * though the builder only exposes one level.
 *
 * There is NO write-time validation of this JSON anywhere — a view authored in or
 * imported from another system is stored verbatim and only validated at render time.
 * The parse/normalise helpers here therefore never throw for business-rule problems;
 * they only report STRUCTURAL (JSON-shape) problems so each consumer can decide how to
 * surface them (the renderer shows a scoped Alert; the builder refuses to render its
 * form). An UNKNOWN widget `type` is tolerated STRUCTURALLY (parseView accepts any
 * object with a string `type`) and surfaces at RENDER TIME as a scoped Alert.
 */

/**
 * A single table column: static header text + a reference to one selected field, plus
 * an OPTIONAL "context link" href template. When set, the renderer draws the cell as a
 * link whose text is the mapped field value and whose href is this template with
 * `{field}` placeholders substituted per row (see TableWidgetView).
 */
export interface TableColumn {
  header: string;
  field: string;
  contextLink?: string;
}

/**
 * Binds one table FIELD to a named VIEW PARAM. At render time, if the named URL param is
 * present and non-empty, an INVISIBLE `filters[<field>][$eq]=<value>` is appended to the
 * table's fetch query (exact match). The operator is ALWAYS `$eq` and is therefore not
 * stored. `field` is a main-collection SCALAR field name or the literal "documentId" (no
 * relation-path nesting). `param` references a view-level param by its `name`; a dangling
 * reference (no matching defined param) is tolerated and simply inert at render.
 */
export interface ParamFilter {
  field: string;
  param: string;
}

/**
 * A SELF-CONTAINED table widget. It owns its own data binding:
 *  - `collection`  : content-type uid, e.g. "api::project.project" (empty allowed).
 *  - `relations`   : relation ATTRIBUTE NAMES on the main content-type to populate
 *                    (one hop, to-one only), e.g. ["stack"].
 *  - `selected`    : field names drawn upon by columns/filters. A MIX of plain main-type
 *                    field names (e.g. "name") and dotted one-hop relation paths of the
 *                    form `<mainCollectionShortName>.<relationName>.<field>`.
 *  - `columns`     : the column mapping (header + selected field + optional contextLink).
 *  - `pageSize`    : OPTIONAL server-side page size. ABSENT => renderer defaults to 10.
 *  - `filterableFields` : OPTIONAL main-collection scalar field names the author marked
 *                    viewer-filterable. ABSENT => no filter bar. The END VIEWER supplies
 *                    the actual values at runtime; those values are NEVER stored.
 *  - `paramFilters` : OPTIONAL bindings of a table field to a named VIEW PARAM. ABSENT =>
 *                    no param filters. Applied INVISIBLY from the URL with operator `$eq`;
 *                    inert if the bound param is absent/empty in the URL.
 */
export interface TableWidget {
  type: "table";
  collection: string;
  relations: string[];
  selected: string[];
  columns: TableColumn[];
  pageSize?: number;
  filterableFields?: string[];
  paramFilters?: ParamFilter[];
}

/** One tab entry: a title plus the (recursive) widget rendered as its content. */
export interface TabEntry {
  title: string;
  widget: Widget;
}

/**
 * A CONTAINER widget: a list of tabs, each holding an arbitrary child widget. Recursive
 * by construction (a tab's `widget` is any Widget, including another TabsWidget).
 */
export interface TabsWidget {
  type: "tabs";
  tabs: TabEntry[];
}

/**
 * Binds ONE record-pinning filter for a `detail` widget. The operator is ALWAYS `$eq`
 * (exact match) and is therefore NOT stored. The matched VALUE comes from one of two
 * sources:
 *  - `source: "literal"` : the fixed string in `value` is used verbatim.
 *  - `source: "param"`   : the value is resolved at render time from the URL view param
 *                          named by `param`. If that param is absent/empty the record is
 *                          treated as UNRESOLVED (the detail shows an INFO message rather
 *                          than fetching an arbitrary/unfiltered record).
 * `field` is a main-collection SCALAR field name or the literal "documentId" (NO
 * relation-path nesting). A dangling `param` reference is tolerated (inert -> info).
 */
export interface RecordFilter {
  field: string;
  source: "literal" | "param";
  value?: string;
  param?: string;
}

/**
 * A SELF-CONTAINED `detail` widget: displays a SINGLE record as a key-value grid (like
 * Ec2InstanceDetail's ColumnLayout block). It REUSES the table's data-binding + field
 * model:
 *  - `collection` : content-type uid (empty allowed).
 *  - `relations`  : one-hop to-one relation attribute names to populate.
 *  - `selected`   : a MIX of plain main scalars, dotted one-hop relation paths
 *                   `<mainShort>.<relation>.<field>`, and "documentId".
 *  - `columns`    : the key-value grid column count (1, 2 or 3).
 *  - `items`      : ordered key-value rows — a static `label` mapped to one `field`
 *                   drawn from `selected`.
 *  - `recordFilters` : the exact-match filters that PIN the view to a single record. Each
 *                   sources its value from a literal or a URL param (operator always $eq).
 * At render it fetches with `pagination[pageSize]=2` to cheaply detect >1; exactly 1 =>
 * grid, 0/>1/missing-param => scoped INFO Alert (never an error).
 */
export interface DetailWidget {
  type: "detail";
  collection: string;
  relations: string[];
  selected: string[];
  columns: 1 | 2 | 3;
  items: { label: string; field: string }[];
  recordFilters: RecordFilter[];
}

/** The widget union. Discriminated on `type`. */
export type Widget = TableWidget | TabsWidget | DetailWidget;

/**
 * A view-level URL PARAM declaration: just a `name` (the URL query-param key). Params are
 * INERT by default — declaring one does nothing unless a table widget attaches a
 * `paramFilter` to it. The value is supplied by the URL at render time and NEVER stored.
 */
export interface ViewParam {
  name: string;
}

/** The full view document stored in the `view` JSON column of a `page`. */
export interface View {
  widgets: Widget[];
  params?: ViewParam[];
}

/** Result of attempting to parse a raw JSON string into a structurally-valid View. */
export type ParseResult =
  | { ok: true; view: View }
  | { ok: false; error: string };

/**
 * Structurally validate one widget at a given JSON path. Returns null on success or a
 * clear per-path error string. Normalises additive-but-missing fields in place (e.g. a
 * table's missing `relations` becomes []). An UNKNOWN `type` is accepted structurally
 * (only a string `type` is required) — the renderer handles it with a scoped Alert.
 */
function validateWidget(w: unknown, path: string): string | null {
  if (!w || typeof w !== "object" || Array.isArray(w)) {
    return `${path} must be an object.`;
  }
  const wo = w as Record<string, unknown>;
  if (typeof wo.type !== "string") {
    return `${path}.type must be a string.`;
  }

  if (wo.type === "table") {
    // collection: lenient — must be a string, empty allowed.
    if (typeof wo.collection !== "string") {
      return `${path}.collection must be a string.`;
    }
    // relations: additive — missing normalises to []. When present must be string[].
    if (wo.relations === undefined) {
      wo.relations = [];
    } else if (!Array.isArray(wo.relations) || !wo.relations.every((s) => typeof s === "string")) {
      return `${path}.relations must be an array of strings.`;
    }
    // selected: required string[].
    if (!Array.isArray(wo.selected) || !wo.selected.every((s) => typeof s === "string")) {
      return `${path}.selected must be an array of strings.`;
    }
    // columns: required array of { header:string, field:string, contextLink?:string }.
    if (!Array.isArray(wo.columns)) {
      return `${path}.columns must be an array.`;
    }
    for (let c = 0; c < wo.columns.length; c++) {
      const col = wo.columns[c] as Record<string, unknown>;
      if (!col || typeof col !== "object" || typeof col.header !== "string" || typeof col.field !== "string") {
        return `${path}.columns[${c}] must have string "header" and "field".`;
      }
      if (col.contextLink !== undefined && typeof col.contextLink !== "string") {
        return `${path}.columns[${c}].contextLink must be a string when present.`;
      }
    }
    // pageSize: additive — must be a number when present.
    if (wo.pageSize !== undefined && typeof wo.pageSize !== "number") {
      return `${path}.pageSize must be a number when present.`;
    }
    // filterableFields: additive — must be string[] when present.
    if (wo.filterableFields !== undefined) {
      if (!Array.isArray(wo.filterableFields) || !wo.filterableFields.every((s) => typeof s === "string")) {
        return `${path}.filterableFields must be an array of strings when present.`;
      }
    }
    // paramFilters: additive — must be an array of { field:string, param:string } when
    // present. STRUCTURAL only: dangling param references / non-existent fields are
    // tolerated (inert at render).
    if (wo.paramFilters !== undefined) {
      if (!Array.isArray(wo.paramFilters)) {
        return `${path}.paramFilters must be an array when present.`;
      }
      for (let p = 0; p < wo.paramFilters.length; p++) {
        const pf = wo.paramFilters[p] as Record<string, unknown>;
        if (!pf || typeof pf !== "object" || Array.isArray(pf) || typeof pf.field !== "string" || typeof pf.param !== "string") {
          return `${path}.paramFilters[${p}] must have string "field" and "param".`;
        }
      }
    }
    return null;
  }

  if (wo.type === "detail") {
    // collection: lenient — must be a string, empty allowed.
    if (typeof wo.collection !== "string") {
      return `${path}.collection must be a string.`;
    }
    // relations: additive — missing normalises to []. When present must be string[].
    if (wo.relations === undefined) {
      wo.relations = [];
    } else if (!Array.isArray(wo.relations) || !wo.relations.every((s) => typeof s === "string")) {
      return `${path}.relations must be an array of strings.`;
    }
    // selected: required string[].
    if (!Array.isArray(wo.selected) || !wo.selected.every((s) => typeof s === "string")) {
      return `${path}.selected must be an array of strings.`;
    }
    // columns: must be the NUMBER 1, 2 or 3.
    if (wo.columns !== 1 && wo.columns !== 2 && wo.columns !== 3) {
      return `${path}.columns must be the number 1, 2 or 3.`;
    }
    // items: required array of { label:string, field:string }.
    if (!Array.isArray(wo.items)) {
      return `${path}.items must be an array.`;
    }
    for (let i = 0; i < wo.items.length; i++) {
      const it = wo.items[i] as Record<string, unknown>;
      if (!it || typeof it !== "object" || Array.isArray(it) || typeof it.label !== "string" || typeof it.field !== "string") {
        return `${path}.items[${i}] must have string "label" and "field".`;
      }
    }
    // recordFilters: required array of { field:string, source:'literal'|'param',
    // value?:string, param?:string }. STRUCTURAL only — lenient-at-write: no minimum of
    // one is enforced and dangling param references are tolerated (inert -> info).
    if (!Array.isArray(wo.recordFilters)) {
      return `${path}.recordFilters must be an array.`;
    }
    for (let i = 0; i < wo.recordFilters.length; i++) {
      const rf = wo.recordFilters[i] as Record<string, unknown>;
      if (!rf || typeof rf !== "object" || Array.isArray(rf) || typeof rf.field !== "string") {
        return `${path}.recordFilters[${i}] must have a string "field".`;
      }
      if (rf.source !== "literal" && rf.source !== "param") {
        return `${path}.recordFilters[${i}].source must be "literal" or "param".`;
      }
      if (rf.value !== undefined && typeof rf.value !== "string") {
        return `${path}.recordFilters[${i}].value must be a string when present.`;
      }
      if (rf.param !== undefined && typeof rf.param !== "string") {
        return `${path}.recordFilters[${i}].param must be a string when present.`;
      }
    }
    return null;
  }

  if (wo.type === "tabs") {
    if (!Array.isArray(wo.tabs)) {
      return `${path}.tabs must be an array.`;
    }
    for (let t = 0; t < wo.tabs.length; t++) {
      const tab = wo.tabs[t] as Record<string, unknown>;
      if (!tab || typeof tab !== "object" || Array.isArray(tab)) {
        return `${path}.tabs[${t}] must be an object.`;
      }
      if (typeof tab.title !== "string") {
        return `${path}.tabs[${t}].title must be a string.`;
      }
      // Recursive: the tab's content is any Widget and must itself validate.
      const childErr = validateWidget(tab.widget, `${path}.tabs[${t}].widget`);
      if (childErr) return childErr;
    }
    return null;
  }

  // Unknown type: tolerated structurally; surfaces at render time as a scoped Alert.
  return null;
}

/**
 * Parse a raw JSON string and structurally validate it against the view contract.
 *
 * "Structural" means: valid JSON, root is an object with a `widgets` array, and each
 * widget validates recursively per `validateWidget`. It does NOT check that referenced
 * content-types/fields exist — that is a render-time concern handled against live schema.
 */
export function parseView(raw: string): ParseResult {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch (e) {
    return { ok: false, error: `Invalid JSON: ${(e as Error).message}` };
  }

  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { ok: false, error: "View must be a JSON object." };
  }

  const obj = data as Record<string, unknown>;
  if (!Array.isArray(obj.widgets)) {
    return { ok: false, error: 'View "widgets" must be an array.' };
  }

  for (let i = 0; i < obj.widgets.length; i++) {
    const err = validateWidget(obj.widgets[i], `widgets[${i}]`);
    if (err) return { ok: false, error: err };
  }

  // params: additive top-level declaration. When present must be an array of objects each
  // with a string `name`. STRUCTURAL only — dangling references from widgets are tolerated.
  if (obj.params !== undefined) {
    if (!Array.isArray(obj.params)) {
      return { ok: false, error: 'View "params" must be an array when present.' };
    }
    for (let i = 0; i < obj.params.length; i++) {
      const p = obj.params[i] as Record<string, unknown>;
      if (!p || typeof p !== "object" || Array.isArray(p) || typeof p.name !== "string") {
        return { ok: false, error: `View "params[${i}]" must be an object with a string "name".` };
      }
    }
  }

  return { ok: true, view: data as unknown as View };
}

/** An empty, contract-shaped view used as the starting point for a new page. */
export function emptyView(): View {
  return { widgets: [] };
}

/** Stable, pretty-printed serialisation used whenever builder state is written back to JSON. */
export function serializeView(view: View): string {
  return JSON.stringify(view, null, 2);
}

/**
 * Read a (possibly dotted) field path from a Strapi record. Plain scalar fields are a
 * flat key lookup; dotted paths walk nested objects (e.g. a populated one-hop relation
 * "stack.name"). Missing links degrade to `undefined` rather than crashing the row
 * render. NOTE: callers reading a stored relation path like "account.stack.name" must
 * first strip the leading main-collection-short-name segment ("account"), which is NOT
 * present on the Strapi record object (the record is shaped as `record.stack.name`).
 */
export function readField(record: Record<string, any>, field: string): unknown {
  if (!record) return undefined;
  if (!field.includes(".")) return record[field];
  return field.split(".").reduce<any>((acc, key) => (acc == null ? undefined : acc[key]), record);
}
